"use client";

import { NearbySUPsClient } from "@/components/nearby-sups/client";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { NearbyProperty } from "@/types/sup";
import { logger } from "@/utils/logger";
import { m } from "framer-motion";
import { RefreshCw } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

interface HotProjectsClientProps {
	initialProjects: NearbyProperty[];
	coordinates: {
		lat: number;
		lng: number;
	};
}

export function HotProjectsClient({ initialProjects, coordinates: pc }: HotProjectsClientProps) {
	const { ref: contentRef, isInView: contentInView } = useScrollAnimation<HTMLHeadingElement>({
		threshold: 0.8,
		triggerOnce: true,
	});

	const [projects] = useState<NearbyProperty[]>(initialProjects);
	const [isLoading] = useState(false);
	const [error] = useState<string | null>(null);

	const handleRetry = () => {
		// For retry, we'll use a default location since we don't have stored coordinates
		// In a real app, you might want to store the last known coordinates
		logger.info("Retrying with default location");
	};

	return (
		<section className="relative py-12 md:py-20 @container" style={{ backgroundColor: "#31202C" }}>
			{/* Top decorative border */}
			<div className="absolute inset-x-0 top-0 w-full h-auto pointer-events-none">
				<div className="w-full h-auto">
					<Image
						src="/images/home/hot-projects/bg.svg"
						alt="Architectural background pattern"
						width={1920}
						height={200}
						className="w-full h-auto max-w-none rotate-180"
						sizes="100dvw"
						loading="lazy"
						unoptimized
					/>
				</div>
			</div>

			{/* Bottom decorative border */}
			<div className="absolute inset-x-0 bottom-0 w-full h-auto pointer-events-none">
				<div className="w-full h-auto">
					<Image
						src="/images/home/hot-projects/bg.svg"
						alt="Architectural background pattern"
						width={1920}
						height={200}
						className="w-full h-auto max-w-none"
						sizes="100dvw"
						loading="lazy"
						unoptimized
					/>
				</div>
			</div>

			<div className="@container px-4 mx-auto relative z-10">
				{/* Section heading with animation */}
				<m.div
					ref={contentRef}
					className="text-center mb-8 md:mb-12"
					initial={{ opacity: 0, y: 20 }}
					animate={contentInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
					transition={{ duration: 0.4, ease: "easeOut", delay: 0.05, type: "tween" }}
					style={{ willChange: contentInView ? "transform, opacity" : "auto" }}
				>
					<h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
						Hot Projects for you
						{isLoading && <RefreshCw className="inline-block ml-3 h-6 w-6 animate-spin" />}
					</h2>
					{error && (
						<div className="text-red-400 text-sm mb-2">
							{error} -{" "}
							<button onClick={handleRetry} className="underline hover:text-red-300">
								Try again
							</button>
						</div>
					)}
					{isLoading && <p className="text-white/70 text-lg">Finding projects near you...</p>}
				</m.div>

				{/* Nearby SUPs using unified client */}
				<div className="relative w-full">
					<NearbySUPsClient
						initialData={projects}
						coordinates={pc}
						loading={isLoading}
						distanceText="your location"
						type="sp"
						shouldPrioritizeCoverImage={false}
						shouldRedirect
					/>
				</div>
			</div>
		</section>
	);
}
